<template>
  <div class="app-nav-drawer">
    <button
      ref="toggleRef"
      type="button"
      class="app-nav-drawer__toggle"
      :aria-expanded="String(isOpen)"
      aria-controls="app-nav-drawer-panel"
      :aria-label="isOpen ? 'Collapse menu' : 'Expand menu'"
      title="Menu"
      @click="toggle"
    >
      <b-icon :icon="isOpen && overlay ? 'x' : 'list'" scale="1.5"></b-icon>
    </button>

    <transition name="app-nav-drawer-fade">
      <div
        v-if="isOpen && overlay"
        class="app-nav-drawer__overlay"
        @click="close()"
      ></div>
    </transition>

    <transition name="app-nav-drawer-slide">
      <nav
        v-show="isOpen || rail"
        id="app-nav-drawer-panel"
        ref="panelRef"
        class="app-nav-drawer__panel"
        :class="{
          'app-nav-drawer__panel--docked': !overlay,
          'app-nav-drawer__panel--rail': rail
        }"
        :style="panelStyle"
        aria-label="Primary"
        @keydown="onPanelKeydown"
      >
        <button
          v-if="overlay"
          type="button"
          class="app-nav-drawer__close"
          aria-label="Close menu"
          data-nav-focusable
          @click="close()"
        >
          <b-icon icon="x" scale="1.5"></b-icon>
        </button>

        <!-- The main menu scrolls; My Account, Help and Support and Sign Out stay pinned at the
             bottom of the rail. -->
        <ul
          v-for="section in sections"
          :key="section.key"
          class="app-nav-drawer__list"
          :class="`app-nav-drawer__list--${section.key}`"
        >
          <li v-for="item in section.items" :key="item.key">
            <template v-if="item.children">
              <button
                type="button"
                class="app-nav-drawer__item app-nav-drawer__item--parent"
                :class="{
                  'is-parent-active': isGroupActive(item, currentRoute),
                  'is-open': isGroupOpen(item.key)
                }"
                :aria-expanded="String(isGroupOpen(item.key) && !rail)"
                :aria-controls="`app-nav-group-${item.key}`"
                :aria-label="rail ? item.label : null"
                v-b-tooltip="railTooltip(item.label)"
                data-nav-focusable
                @click="onParentClick(item)"
              >
                <b-icon v-if="item.icon" :icon="item.icon" class="app-nav-drawer__icon"></b-icon>
                <span class="app-nav-drawer__label">{{ item.label }}</span>
                <b-icon
                  icon="chevron-down"
                  class="app-nav-drawer__chevron"
                  :class="{ 'is-open': isGroupOpen(item.key) }"
                  aria-hidden="true"
                ></b-icon>
              </button>

              <transition
                name="app-nav-drawer-expand"
                @enter="onSublistEnter"
                @after-enter="onSublistAfterTransition"
                @leave="onSublistLeave"
                @after-leave="onSublistAfterTransition"
              >
                <ul
                  v-show="isGroupOpen(item.key) && !rail"
                  :id="`app-nav-group-${item.key}`"
                  class="app-nav-drawer__sublist"
                >
                  <li v-for="child in item.children" :key="child.key">
                    <a
                      v-if="child.href"
                      :href="child.href"
                      :target="child.external ? '_blank' : null"
                      :rel="child.external ? 'noopener noreferrer' : null"
                      class="app-nav-drawer__item app-nav-drawer__item--child"
                      :tabindex="childTabindex(item.key)"
                      data-nav-focusable
                      @click="onNavigate"
                    >
                      <span class="app-nav-drawer__label">{{ child.label }}</span>
                    </a>
                    <button
                      v-else-if="!child.route"
                      type="button"
                      class="app-nav-drawer__item app-nav-drawer__item--child"
                      :disabled="isDisabled(child)"
                      :tabindex="childTabindex(item.key)"
                      data-nav-focusable
                      @click="onAction(child)"
                    >
                      <span class="app-nav-drawer__label">{{ child.label }}</span>
                      <b-icon
                        v-if="child.animate && child.animate(context)"
                        icon="arrow-clockwise"
                        animation="spin"
                        aria-hidden="true"
                      ></b-icon>
                    </button>
                    <router-link
                      v-else
                      :to="child.route"
                      class="app-nav-drawer__item app-nav-drawer__item--child"
                      :class="{ 'is-active': isItemActive(child, currentRoute) }"
                      :aria-current="isItemActive(child, currentRoute) ? 'page' : null"
                      :tabindex="childTabindex(item.key)"
                      data-nav-focusable
                      @click.native="onLeaf(child)"
                    >
                      <span class="app-nav-drawer__label">{{ child.label }}</span>
                      <b-icon
                        v-if="child.badge && child.badge(context)"
                        icon="exclamation-triangle-fill"
                        class="text-warning"
                        aria-label="Needs attention"
                      ></b-icon>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </template>

            <router-link
              v-else
              :to="item.route"
              class="app-nav-drawer__item"
              :class="{ 'is-active': isItemActive(item, currentRoute) }"
              :aria-current="isItemActive(item, currentRoute) ? 'page' : null"
              :aria-label="rail ? item.label : null"
              v-b-tooltip="railTooltip(item.label)"
              data-nav-focusable
              @click.native="onLeaf(item)"
            >
              <b-icon v-if="item.icon" :icon="item.icon" class="app-nav-drawer__icon"></b-icon>
              <span class="app-nav-drawer__label">{{ item.label }}</span>
            </router-link>
          </li>

          <li v-if="'bottom' === section.key" class="app-nav-drawer__signout">
            <button
              type="button"
              class="app-nav-drawer__item"
              :aria-label="rail ? 'Sign Out' : null"
              v-b-tooltip="railTooltip('Sign Out')"
              data-nav-focusable
              @click="$emit('logout')"
            >
              <b-icon icon="box-arrow-right" class="app-nav-drawer__icon"></b-icon>
              <span class="app-nav-drawer__label">Sign Out</span>
            </button>
          </li>
        </ul>
      </nav>
    </transition>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from '@vue/composition-api'
import { useRoute } from 'vue2-helpers/vue-router'

import { useNavDrawer } from '@composables/useNavDrawer'
import { useNavMenu } from '@composables/useNavMenu'
import {
  findActiveGroupKey,
  isGroupActive,
  isItemActive,
  isItemDisabled
} from '@/config/navMenu'

const FOCUSABLE = '[data-nav-focusable]:not([tabindex="-1"]):not(:disabled)'

export default defineComponent({
  name: 'AppNavDrawer',
  setup(_, { emit }) {
    const route = useRoute()
    const drawer = useNavDrawer()
    // The same filtered menu the home page tiles use; AppNavbar supplies its own flags.
    const { context, menu } = useNavMenu()

    const toggleRef = ref(null)
    const panelRef = ref(null)
    // Groups are opened and closed independently - several can be open at once (LIQ-2643).
    const openGroups = ref([])

    const isOpen = computed(() => drawer.state.expanded)
    const overlay = computed(() => drawer.state.overlayMode)
    // Collapsed on desktop: icons only, each labelled by a tooltip (LIQ-2765).
    const rail = drawer.isRail

    // Name, query and params - the Analytics reports all open the Dashboard, told apart by report.
    const currentRoute = computed(() => ({
      name: route.name,
      query: route.query,
      params: route.params
    }))
    const routeFullPath = computed(() => route.fullPath)

    // The main menu, then the items pinned to the bottom of the rail (see navMenu.js `bottom`).
    const sections = computed(() => [
      { key: 'main', items: menu.value.filter((item) => !item.bottom) },
      { key: 'bottom', items: menu.value.filter((item) => item.bottom) }
    ])

    const activeGroupKey = computed(() =>
      findActiveGroupKey(menu.value, currentRoute.value)
    )

    const isGroupOpen = (key) => openGroups.value.includes(key)

    const toggleGroup = (key) => {
      openGroups.value = isGroupOpen(key)
        ? openGroups.value.filter((k) => k !== key)
        : [...openGroups.value, key]
    }

    // In the rail a group has no room for its items, so its icon expands the panel with that
    // group open instead.
    const onParentClick = (item) => {
      if (!rail.value) return toggleGroup(item.key)
      if (!isGroupOpen(item.key)) openGroups.value = [...openGroups.value, item.key]
      drawer.open()
    }

    // Items of a closed group, or of any group while the rail hides them, are skipped by Tab.
    const childTabindex = (key) => (isGroupOpen(key) && !rail.value ? 0 : -1)

    const railTooltip = (title) => ({
      title,
      placement: 'right',
      trigger: 'hover focus',
      boundary: 'window',
      disabled: !rail.value
    })

    // The current page's group is opened on load and on every navigation; the others stay as
    // the user left them.
    const openActiveGroup = () => {
      const key = activeGroupKey.value
      if (key && !isGroupOpen(key)) openGroups.value = [...openGroups.value, key]
    }

    // Docked: sits under the header and follows it on scroll. Overlay: covers it.
    const panelStyle = computed(() =>
      overlay.value ? {} : { top: `${drawer.state.top}px` }
    )

    const focusables = () =>
      panelRef.value ? Array.from(panelRef.value.querySelectorAll(FOCUSABLE)) : []

    const open = async () => {
      openActiveGroup()
      drawer.open()

      // Only the overlay takes focus; the docked panel leaves it on the toggle.
      if (!overlay.value) return

      await nextTick()
      const active = panelRef.value.querySelector('[aria-current="page"]')
      ;(active || focusables()[0] || toggleRef.value).focus()
    }

    const close = ({ restoreFocus = true } = {}) => {
      if (!isOpen.value) return
      drawer.close()
      if (restoreFocus && overlay.value && toggleRef.value) toggleRef.value.focus()
    }

    const toggle = () => (isOpen.value ? close() : open())

    // The overlay closes after a choice; the docked panel stays put.
    const afterChoice = () => {
      if (overlay.value) close({ restoreFocus: false })
    }

    const onNavigate = () => afterChoice()

    const onLeaf = (item) => {
      if (item.action) emit('action', item.action)
      afterChoice()
    }

    const onAction = (item) => {
      emit('action', item.action)
      afterChoice()
    }

    const isDisabled = (item) => isItemDisabled(item, context.value)

    // A group's items slide open and closed (height and padding animate between 0 and their
    // natural size). Without a CSS transition (reduced motion) Vue finishes straight away.
    const onSublistEnter = (el) => {
      const height = el.scrollHeight
      el.style.height = '0'
      el.style.paddingTop = '0'
      el.style.paddingBottom = '0'
      void el.offsetHeight // apply the collapsed size before animating to the full one
      el.style.height = `${height}px`
      el.style.paddingTop = ''
      el.style.paddingBottom = ''
    }

    const onSublistLeave = (el) => {
      el.style.height = `${el.scrollHeight}px`
      void el.offsetHeight
      el.style.height = '0'
      el.style.paddingTop = '0'
      el.style.paddingBottom = '0'
    }

    const onSublistAfterTransition = (el) => {
      el.style.height = ''
      el.style.paddingTop = ''
      el.style.paddingBottom = ''
    }

    const onPanelKeydown = (e) => {
      // Esc dismisses the overlay only; the docked panel is dismissed with the toggle.
      if (e.key === 'Escape') {
        if (overlay.value) {
          e.stopPropagation()
          close()
        }
        return
      }

      const items = focusables()
      if (!items.length) return
      const idx = items.indexOf(document.activeElement)

      if (e.key === 'Tab') {
        // Focus trap while the overlay is open.
        if (!overlay.value) return

        const first = items[0]
        const last = items[items.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        items[(idx + 1) % items.length].focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        items[(idx - 1 + items.length) % items.length].focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        items[0].focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        items[items.length - 1].focus()
      }
    }

    watch(activeGroupKey, openActiveGroup, { immediate: true })

    // Watch fullPath, not name: Back/Forward between /dashboard/1 and /dashboard/2
    // keeps the name but must still dismiss the overlay and focus trap.
    watch(routeFullPath, () => afterChoice())

    onMounted(() => drawer.register())
    onBeforeUnmount(() => drawer.unregister())

    return {
      toggleRef,
      panelRef,
      isOpen,
      overlay,
      rail,
      currentRoute,
      context,
      sections,
      panelStyle,
      toggle,
      close,
      isGroupOpen,
      toggleGroup,
      onParentClick,
      childTabindex,
      railTooltip,
      onNavigate,
      onLeaf,
      onAction,
      isDisabled,
      onSublistEnter,
      onSublistLeave,
      onSublistAfterTransition,
      onPanelKeydown,
      isItemActive,
      isGroupActive
    }
  }
})
</script>

<style lang="scss" scoped>
// Styled after the Q Portal's left menu (LIQ-2765), from the LimeIQ Design System tokens
// (assets/sass/custom/_brand-tokens.scss): deep cyan rail with mint text, an open group sits in a
// lighter block under a mint rule, and the current page is marked with a mint square.
$drawer-width: 272px;
$drawer-width-narrow: 236px;
$rail-width: 64px;
$on-rail-hover: rgba(144, 255, 207, 0.1);
$group-block: rgba(255, 255, 255, 0.06);

.app-nav-drawer {
  display: inline-flex;
  align-items: center;
  font-family: var(--liq-font-sans);

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-left: -8px;
    border: 0;
    border-radius: var(--liq-radius-sm);
    background: transparent;
    // White like everything on the top row; hover is a soft light wash rather than a colour.
    color: var(--liq-on-header);
    cursor: pointer;
    transition: background 0.18s var(--liq-ease);

    &:hover {
      background: var(--liq-on-header-hover);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--liq-on-header);
      outline-offset: 2px;
    }
  }

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: 1040;
    background: rgba(32, 52, 53, 0.45);
  }

  &__panel {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 1050;
    // A column: the main list scrolls, the bottom section stays pinned below it.
    display: flex;
    flex-direction: column;
    width: $drawer-width;
    max-width: 100%;
    padding: 12px 10px;
    overflow: hidden;
    background: var(--liq-deep);
    color: var(--liq-mint);
    box-shadow: 2px 0 12px rgba(32, 52, 53, 0.2);
    transition: width 200ms ease-out;

    // Docked beside the page: under the header, no shadow.
    &--docked {
      z-index: 1030;
      box-shadow: none;
    }

    @media (max-width: 960px) {
      width: $drawer-width-narrow;
    }

    // Mobile: full-height, 100% wide.
    @media (max-width: 575.98px) {
      width: 100%;
    }

    // Collapsed on desktop: icons only, centred; labels come from tooltips.
    &--rail {
      width: $rail-width;
      padding-right: 8px;
      padding-left: 8px;

      .app-nav-drawer__item {
        justify-content: center;
        padding: 0;
      }

      .app-nav-drawer__icon {
        margin-right: 0;
      }

      .app-nav-drawer__label,
      .app-nav-drawer__chevron {
        display: none;
      }

      .app-nav-drawer__item--parent.is-open {
        border-bottom-color: transparent;
        border-radius: var(--liq-radius-sm);
      }

      // Without labels, the current page's group is marked like an active page.
      .app-nav-drawer__item.is-parent-active {
        background: var(--liq-mint);
        color: var(--liq-deep);
      }
    }
  }

  // The overlay covers the header toggle (always on mobile), so it carries its own.
  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: 0 0 4px auto;
    border: 0;
    border-radius: var(--liq-radius-sm);
    background: transparent;
    color: var(--liq-mint);
    cursor: pointer;

    &:hover {
      background: $on-rail-hover;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--liq-mint);
      outline-offset: -2px;
    }
  }

  &__list,
  &__sublist {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__list--main {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  // My Account, Help and Support, Sign Out - pinned to the bottom of the rail.
  &__list--bottom {
    flex: none;
    max-height: 50%;
    margin-top: 10px;
    padding-top: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    border-top: 1px solid var(--liq-hair-mint);
  }

  &__sublist {
    padding: 2px 0 6px;
    background: $group-block;
  }

  &__item {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 42px;
    padding: 0 12px;
    border: 0;
    border-radius: var(--liq-radius-sm);
    background: transparent;
    color: var(--liq-mint);
    font-size: 15px;
    text-align: left;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s var(--liq-ease);

    &:hover {
      background: $on-rail-hover;
      color: var(--liq-mint);
      text-decoration: none;
    }

    // No outline after a mouse click (Bootstrap's reboot gives every focused button one) - the
    // keyboard focus ring below stays, for keyboard users.
    &:focus {
      outline: none;
    }

    // currentColor: mint on the dark rail, deep cyan on the mint active items, so the ring
    // shows on both.
    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: -2px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    // A top-level page (Home) on screen: mint block, deep cyan text - never white on mint.
    &.is-active,
    &.is-active:hover {
      background: var(--liq-mint);
      color: var(--liq-deep);
      font-weight: 500;
    }

    &.is-parent-active {
      font-weight: 500;
    }

    // An open group joins its items' block, underlined in mint like the Q Portal.
    &--parent.is-open {
      border-bottom: 1px solid var(--liq-mint);
      border-radius: var(--liq-radius-sm) var(--liq-radius-sm) 0 0;
      background: $group-block;
    }

    &--child {
      position: relative;
      min-height: 38px;
      padding-left: 40px;
      border-radius: 0;
      color: var(--liq-mint);
      font-size: 14.5px;
      opacity: 0.75;

      &:hover {
        opacity: 1;
      }

      // The current page: full-strength mint with a square marker.
      &.is-active,
      &.is-active:hover {
        background: transparent;
        color: var(--liq-mint);
        opacity: 1;

        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 22px;
          width: 7px;
          height: 7px;
          margin-top: -3.5px;
          background: var(--liq-mint);
        }
      }
    }
  }

  &__icon {
    flex: none;
    margin-right: 10px;
    font-size: 18px;
  }

  &__label {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chevron {
    flex: none;
    color: var(--liq-mint);
    transition: transform 0.18s var(--liq-ease);

    &.is-open {
      transform: rotate(180deg);
    }
  }

}

.app-nav-drawer-slide-enter-active,
.app-nav-drawer-slide-leave-active {
  transition: transform 200ms ease-out;
}
.app-nav-drawer-slide-enter,
.app-nav-drawer-slide-leave-to {
  transform: translateX(-100%);
}
.app-nav-drawer-fade-enter-active,
.app-nav-drawer-fade-leave-active {
  transition: opacity 200ms ease-out;
}
.app-nav-drawer-expand-enter-active,
.app-nav-drawer-expand-leave-active {
  overflow: hidden;
  transition: height 180ms var(--liq-ease), padding 180ms var(--liq-ease);
}
.app-nav-drawer-fade-enter,
.app-nav-drawer-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .app-nav-drawer-slide-enter-active,
  .app-nav-drawer-slide-leave-active,
  .app-nav-drawer-fade-enter-active,
  .app-nav-drawer-fade-leave-active,
  .app-nav-drawer-expand-enter-active,
  .app-nav-drawer-expand-leave-active,
  .app-nav-drawer__chevron,
  .app-nav-drawer__item,
  .app-nav-drawer__panel {
    transition: none;
  }
}
</style>
